'use client';

import {
  IconCheck,
  IconMessage,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react';
import { useState } from 'react';

import { Button } from '@j-s/components';
import { ContactMessage } from '@j-s/dr-shared';

interface TicketReplyFormProps {
  ticket: ContactMessage;
  onSendReply: (text: string) => Promise<void>;
  onReopen: () => Promise<void>;
}

export const TicketReplyForm = ({
  ticket,
  onSendReply,
  onReopen,
}: TicketReplyFormProps) => {
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim() || isSending) return;

    setIsSending(true);
    try {
      await onSendReply(replyText.trim());
      setReplyText('');
    } finally {
      setIsSending(false);
    }
  };

  if (
    ticket.status === 'phone_call' ||
    (ticket.contactMethod === 'phone' &&
      ticket.phoneResponsePreference === 'call')
  ) {
    return (
      <div className="p-3.5 bg-amber-50 rounded-2xl border border-amber-200 flex items-center gap-2.5 text-xs text-amber-900">
        <IconCheck size={18} className="text-amber-600 shrink-0" />
        <span>
          Dla tego zgłoszenia wybrano kontakt telefoniczny. Pracownik serwisu
          skontaktuje się z Tobą pod wskazanym numerem telefonu.
        </span>
      </div>
    );
  }

  if (ticket.status === 'closed') {
    return (
      <div className="p-3.5 bg-zinc-100 rounded-2xl border border-zinc-200 flex flex-col sm:flex-row items-center justify-between gap-2.5 text-xs text-zinc-600">
        <div className="flex items-center gap-2">
          <IconCheck size={18} className="text-zinc-600 shrink-0" />
          <span>
            {ticket.closedBy === 'admin'
              ? 'Serwis oznaczył tę sprawę jako zakończoną.'
              : 'Sprawa została oznaczona jako zakończona.'}
          </span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onPress={onReopen}
          startContent={<IconRefresh size={14} />}
        >
          Wznów sprawę
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-2 pt-2">
      <label
        htmlFor="ticket-reply-text"
        className="text-xs font-semibold text-gray-700 flex items-center gap-1.5"
      >
        <IconMessage size={14} />
        <span>Dopisz kolejną wiadomość w wątku:</span>
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <textarea
          id="ticket-reply-text"
          rows={2}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Wpisz treść wiadomości..."
          aria-label="Wpisz treść wiadomości"
          className="flex-1 px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-base outline-none focus:border-red-500 focus:bg-white transition-all resize-none"
        />
        <Button
          type="submit"
          variant="primary"
          size="md"
          isDisabled={!replyText.trim() || isSending}
          isLoading={isSending}
          startContent={<IconSend size={16} />}
        >
          Wyślij
        </Button>
      </div>
    </form>
  );
};
